import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader } from "../ui/dialog";
import { PaymentInfoForm, PaymentMethodData } from "./PaymentInfoForm";
import { countFilledMethods } from "../../utils/payoutMethods";

interface PayoutMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Saves the filled methods. Throw to keep the modal open; resolve to close
   * it and fire `onSaved`. Affiliates and agencies pass their own save call.
   */
  onSave: (methods: PaymentMethodData[]) => Promise<void>;
  /** Runs after a successful save — retry the payout request, refetch, etc. */
  onSaved?: () => void | Promise<void>;
  /** What the affiliate already has on file, so nothing is silently dropped. */
  initialData?: PaymentMethodData[];
  title?: string;
  description?: string;
  /** Method names the paying brand accepts. Empty = show every method. */
  allowedMethodNames?: string[];
  saveLabel?: string;
}

/**
 * The one place an affiliate or agency is asked for payout details: opened
 * inline at the moment money is about to move, filtered to the methods the
 * payer can actually use. Never shown at signup.
 */
export function PayoutMethodModal({
  isOpen,
  onClose,
  onSave,
  onSaved,
  initialData = [],
  title = "Where should we send your payout?",
  description,
  allowedMethodNames = [],
  saveLabel = "Save and continue",
}: PayoutMethodModalProps) {
  const [data, setData] = useState<PaymentMethodData[]>(initialData);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset the draft each time the modal opens with fresh initial data.
  useEffect(() => {
    if (isOpen) {
      setData(initialData);
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = async () => {
    // Only send methods with a value; the backend replaces the whole set.
    const filled = data
      .map((m) => ({
        paymentMethodId: m.paymentMethodId,
        fieldValues: m.fieldValues.filter(
          (f) => f.value && f.value.trim() !== ""
        ),
      }))
      .filter((m) => m.fieldValues.length > 0);

    if (countFilledMethods(filled) < 1) {
      setError("Fill in at least one payment method");
      return;
    }

    try {
      setIsSaving(true);
      setError("");
      await onSave(filled);
      toast.success("Payout method saved");
      onClose();
      await onSaved?.();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Could not save your payout method. Please try again.";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const resolvedDescription =
    description ??
    (allowedMethodNames.length > 0
      ? `This brand pays with ${allowedMethodNames.join(", ")}. Add one of them and you're set.`
      : "Add at least one payout method. You can add more in Settings any time.");

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSaving && onClose()}>
      <DialogContent>
        <DialogHeader showCloseButton onClose={onClose}>
          {title}
        </DialogHeader>
        <p className="text-sm text-muted-foreground mb-4">{resolvedDescription}</p>
        <PaymentInfoForm
          paymentMethodsData={data}
          onChangePaymentMethodsData={setData}
          showTitle={false}
          showSaveButton
          onSave={handleSave}
          onCancel={onClose}
          isLoading={isSaving}
          error={error}
          highlightedMethods={allowedMethodNames}
          filterMethods={allowedMethodNames}
          minMethods={1}
          saveLabel={saveLabel}
        />
      </DialogContent>
    </Dialog>
  );
}
